import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Typography,
  IconButton,
  Grid,
} from '@material-ui/core';
import { Close as CloseIcon, Add, RemoveCircleOutline } from '@material-ui/icons';
import { TabletButton } from '@campfire/tablet-button';

interface PollDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (content: string, metadata: Record<string, unknown>) => void;
}

export const PollDialog = ({ open, onClose, onSubmit }: PollDialogProps) => {
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState(['', '']);

  const handleAddOption = () => {
    setOptions((prev) => [...prev, '']);
  };

  const handleRemoveOption = (index: number) => {
    setOptions((prev) => prev.filter((_unused, i) => i !== index));
  };

  const handleOptionChange = (index: number, value: string) => {
    setOptions((prev) => prev.map((o, i) => (i === index ? value : o)));
  };

  const handleSubmit = () => {
    const validOptions = options.filter((o) => o.trim());
    if (!question.trim() || validOptions.length < 2) return;

    onSubmit(question.trim(), {
      type: 'poll',
      question: question.trim(),
      options: validOptions,
      votes: {},
      closed: false,
    });
    handleClose();
  };

  const handleClose = () => {
    setQuestion('');
    setOptions(['', '']);
    onClose();
  };

  const validOptions = options.filter((o) => o.trim());

  if (!open) return null;

  return (
    <Dialog
      open
      onClose={handleClose}
      maxWidth='sm'
      fullWidth
      PaperProps={{ style: { borderRadius: 16, overflow: 'hidden' } }}
    >
      <DialogTitle style={{ paddingBottom: 0 }}>
        <Grid container justify='space-between' alignItems='flex-start'>
          <Grid item>
            <Typography style={{ fontWeight: 700, fontSize: '1.15rem', color: '#333' }}>Create Poll</Typography>
            <Typography variant='body2' style={{ color: '#999', marginTop: 2 }}>
              Ask a question and let members vote
            </Typography>
          </Grid>
          <Grid item>
            <IconButton aria-label='Close' size='small' onClick={handleClose} style={{ marginTop: -4 }}>
              <CloseIcon style={{ fontSize: 20, color: '#999' }} />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent style={{ paddingTop: 16, paddingBottom: 8 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label='Question'
              variant='outlined'
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              required
              InputProps={{ style: { borderRadius: 10 } }}
            />
          </Grid>
          {options.map((option, idx) => (
            // eslint-disable-next-line react/no-array-index-key
            <Grid item xs={12} key={idx}>
              <Box display='flex' alignItems='center' style={{ gap: 8 }}>
                <TextField
                  fullWidth
                  label={`Option ${idx + 1}`}
                  variant='outlined'
                  value={option}
                  onChange={(e) => handleOptionChange(idx, e.target.value)}
                  InputProps={{ style: { borderRadius: 10 } }}
                />
                {options.length > 2 && (
                  <IconButton size='small' onClick={() => handleRemoveOption(idx)}>
                    <RemoveCircleOutline style={{ color: '#ef5350', fontSize: 20 }} />
                  </IconButton>
                )}
              </Box>
            </Grid>
          ))}
          <Grid item xs={12}>
            <TabletButton variant='text' color='primary' onClick={handleAddOption} startIcon={<Add />}>
              Add Option
            </TabletButton>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions style={{ padding: '8px 20px 16px' }}>
        <TabletButton variant='text' onClick={handleClose} style={{ marginRight: 8, color: '#999' }}>
          Cancel
        </TabletButton>
        <TabletButton
          variant='contained'
          color='primary'
          onClick={handleSubmit}
          disabled={!question.trim() || validOptions.length < 2}
        >
          Send Poll
        </TabletButton>
      </DialogActions>
    </Dialog>
  );
};
